import React from 'react';
import { BrandedButton } from './BrandedButton';
import { KratosFlow } from '../services/kratosService';
import './LinkedAccounts.css';

interface LinkedAccountsProps {
  flow: KratosFlow;
  /** Submit one settings method; resolves once the parent has the new flow. */
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy: boolean;
}

/**
 * Linked accounts (social sign-in), driven by the Kratos settings flow's
 * `oidc` nodes: `link` for each provider not yet linked, `unlink` for each
 * linked one — Kratos leaves `unlink` out when it's the account's only way
 * to sign in. Nothing is shown when no provider is configured.
 */
export const LinkedAccounts: React.FC<LinkedAccountsProps> = ({ flow, onSubmit, busy }) => {
  const nodes = flow.ui.nodes.filter(
    (node) =>
      node.group === 'oidc' &&
      ['link', 'unlink'].includes(node.attributes?.name) &&
      typeof node.attributes.value === 'string'
  );
  if (nodes.length === 0) return null;

  const errors = nodes
    .flatMap((node) => node.messages ?? [])
    .filter((message) => message.type === 'error');

  return (
    <section className="settings-form" aria-labelledby="linked-accounts-title">
      <h3 id="linked-accounts-title" className="settings-section-title">
        Linked accounts
      </h3>

      {errors.map((message) => (
        <div key={message.id} className="settings-error" role="alert">
          {message.text}
        </div>
      ))}

      <p className="settings-help">
        Sign in with another account you already have. Linking takes you to that provider to
        confirm it&apos;s yours, then brings you back here.
      </p>

      <div className="settings-linked-accounts">
        {nodes.map((node) => (
          <BrandedButton
            key={`${node.attributes.name}-${node.attributes.value}`}
            type="button"
            variant={node.attributes.name === 'link' ? 'secondary' : 'outline'}
            fullWidth
            disabled={busy || node.attributes.disabled}
            onClick={() => onSubmit({ method: 'oidc', [node.attributes.name]: node.attributes.value })}
          >
            {node.meta?.label?.text ?? `${node.attributes.name} ${node.attributes.value}`}
          </BrandedButton>
        ))}
      </div>
    </section>
  );
};
